import React from 'react';
import { ArrowUp, Github, Instagram, Linkedin, Heart, Terminal } from 'lucide-react';
import { portfolioData } from '../data/portfolioData';

const TikTokIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M19.589 6.686a4.793 4.793 0 0 1-3.77-4.245V2h-3.445v13.672a2.896 2.896 0 0 1-2.9 2.892 2.896 2.896 0 0 1-2.892-2.9 2.896 2.896 0 0 1 2.892-2.892c.31 0 .61.05.89.143V9.43a6.326 6.326 0 0 0-.89-.063 6.338 6.338 1 0 6.337 6.337V8.841a8.217 8.217 0 0 0 4.778 1.516v-3.67a4.83 4.83 0 0 1-1.000-.001z"/>
  </svg>
);

const SpotifyIcon = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.48-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.301 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.72-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.62.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
  </svg>
);

export default function Footer() {
  const { profile } = portfolioData;

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-slate-100 dark:bg-slate-950 border-t border-slate-200 dark:border-slate-800/80 py-12 relative transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pb-8 border-b border-slate-200 dark:border-slate-800/60">
          
          {/* Logo & Domain */}
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Synthe Logo" className="w-9 h-9 object-contain" />
            <div>
              <div className="font-bold text-slate-900 dark:text-white text-base flex items-center gap-2">
                Synthe
                <span className="text-xs px-2 py-0.5 rounded bg-white dark:bg-slate-900 text-brand-bronze font-mono border border-slate-200 dark:border-slate-800">
                  djsynthe.com
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 font-mono">
                Hosted on GitHub Pages • Zero Server Overhead
              </p>
            </div>
          </div>

          {/* Nav Quick Links & Back to Top */}
          <div className="flex flex-wrap items-center gap-4 sm:gap-5">
            <a
              href={profile.socials.spotify}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-mono text-slate-600 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 flex items-center gap-1.5 transition-colors"
            >
              <SpotifyIcon className="w-4 h-4 text-emerald-500" /> Spotify
            </a>

            <a
              href={profile.socials.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-mono text-slate-600 dark:text-slate-400 hover:text-cyan-600 dark:hover:text-cyan-400 flex items-center gap-1.5 transition-colors"
            >
              <TikTokIcon className="w-4 h-4 text-cyan-500" /> TikTok
            </a>

            <a
              href={profile.socials.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-mono text-slate-600 dark:text-slate-400 hover:text-pink-600 dark:hover:text-pink-400 flex items-center gap-1.5 transition-colors"
            >
              <Instagram className="w-4 h-4 text-pink-500" /> Instagram
            </a>

            <a
              href={profile.socials.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-mono text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 flex items-center gap-1.5 transition-colors"
            >
              <Linkedin className="w-4 h-4 text-blue-500" /> LinkedIn
            </a>

            <a
              href={profile.socials.github}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-mono text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white flex items-center gap-1.5 transition-colors"
            >
              <Github className="w-4 h-4" /> GitHub
            </a>

            <button
              onClick={scrollToTop}
              className="p-2.5 rounded-xl bg-slate-900 border border-slate-800 text-slate-400 hover:text-brand-bronze hover:border-slate-700 transition-all"
              title="Back to Top"
            >
              <ArrowUp className="w-4 h-4" />
            </button>
          </div>

        </div>

        {/* Copyright & Amazon Persona Nod */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400 font-mono">
          <div>
            © {new Date().getFullYear()} Synthe. All rights reserved.
          </div>
          <div className="flex items-center gap-1.5 text-slate-400">
            <span>Crafted with</span>
            <Heart className="w-3.5 h-3.5 text-red-500 fill-red-500 inline" />
            <span>+ Sour Candy in the Studio</span>
          </div>
        </div>

      </div>
    </footer>
  );
}
